import { useEffect, useRef } from "react";
import { useAppData } from "../state/AppData";
import { formatNumber, formatRate, parseTimestamp, relativeTime } from "../lib/format";
import { freshnessOf } from '../lib/definitions/health';
import { packetInProvenanceScope, PROVENANCE_SCOPE_LABEL } from '../lib/definitions/trust';
import { PROTOCOL_LABEL } from '../lib/definitions/protocols';
import { resolveNodeProtocol } from "../lib/nodeProtocol";
import { regionInScope } from "../lib/regions";
import { ProtocolToggle } from "./ProtocolToggle";
import { ProvenanceToggle } from "./ProvenanceToggle";
import { RegionFilter } from "./RegionFilter";
import { RegionReachControl } from "./RegionReachControl";
import { TagFilter } from "./TagFilter";
import type { MeshNode } from "../api/types";

export function StatusStrip() {
  const {
    stats,
    scopedStats,
    statsEverLoaded,
    staleNote,
    health,
    wsState,
    overflowCount,
    nodes,
    packets,
    protocolFilter,
    provenanceScope,
    selectedRegions,
    regionsForNode,
  } = useAppData();
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Real status-strip bottom edge as a CSS variable, kept live via
  // ResizeObserver — the same pattern App.tsx's Shell already uses for
  // --topbar-h (2026-09-20's phone-width panel-anchoring fix), needed here
  // because RegionFilter's popover moved INTO this row 2026-09-21 — a
  // per-view filter up in the top bar made it feel like it wasn't part of
  // the current "module"; the top bar is global chrome. At narrow
  // widths this strip wraps to multiple lines just like the topbar does,
  // so the popover's own mobile fallback (position:fixed, pinned below
  // the controls) now anchors to THIS element's measured bottom, not the
  // topbar's — see .region-filter-menu's own comment in global.css.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const apply = () => document.documentElement.style.setProperty("--live-controls-bottom", `${el.getBoundingClientRect().bottom}px`);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    window.addEventListener("resize", apply);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", apply);
    };
  }, []);

  const wsLabel =
    wsState === "open" ? "Live" : wsState === "connecting" ? "Connecting" : wsState === "retrying" ? "Reconnecting" : "Disconnected";

  const protocolActive = protocolFilter !== "all";
  const regionActive = selectedRegions.size > 0;
  // Provenance scope is a PACKET-level concept (trust_tier belongs to an
  // observation, not the node itself — a node can be heard by our own
  // radio AND the firehose over its lifetime) — applied to the packet
  // counts below, deliberately not to the node roster counts, same scope
  // decision as the map's own node-dots layer (LiveMap.tsx).
  const provenanceActive = provenanceScope === "connected_radio";
  const filtered = protocolActive || regionActive || provenanceActive;

  // /api/stats now answers BOTH protocol and provenance scope for real
  // (2026-09-21, two rounds: first protocol — the packets total for
  // "all" reported 31,465 while meshtastic and meshcore each showed only
  // about 100 — then provenance, since the headline total needed to
  // aggregate data from CoreScope as well, i.e. the headline total must include meshview's/
  // CoreScope's firehose tables under "firehose" scope, not just
  // first-party+MQTT `packets` — see api/routes/stats.py's own docstring
  // for the exact contract, which mirrors packetInProvenanceScope/
  // TRUST_TIER_INFO.ownRadio so the counter and the map never disagree
  // about what a given provenance scope includes). Region is the ONE axis
  // still computed client-side (region membership has no server concept),
  // so it's the only case that still needs the live-buffer fallback below,
  // clearly labelled "(session)" rather than presented as the same "total"
  // an unfiltered view shows — the counts must
  // clearly indicate when a filter is active, so nobody misreads a filtered count
  // as the total"). Node counts are computed live from the full roster we
  // already hold client-side (accurate for every filter combination;
  // provenance deliberately doesn't apply to them — a node is a reconciled
  // identity, not a per-observation trust tier).
  let nodesTotal = stats?.nodes_total;
  let nodesActive = stats?.nodes_active;
  let packetsTotal = stats?.packets_total;
  let packetsRate = stats?.packets_per_min;
  let packetsLabel = "Packets";
  let rateLabel = "Packets/min";
  let nodesLabel = "Nodes";
  let activeLabel = "Active";

  if (filtered) {
    const nodeMatches = (n: MeshNode) => {
      if (protocolActive && resolveNodeProtocol(n) !== protocolFilter) return false;
      if (regionActive && !regionInScope({ lat: n.lat, lon: n.lon }, { selectedCount: selectedRegions.size, regionsForNode })) return false;
      return true;
    };

    let total = 0;
    let active = 0;
    for (const n of nodes.values()) {
      if (!nodeMatches(n)) continue;
      total += 1;
      if (freshnessOf(n.last_heard) === "online") active += 1;
    }
    nodesTotal = total;
    nodesActive = active;

    // Node tags exclude provenance on purpose — the roster count above is
    // NOT scoped by it (see the comment on provenanceActive above), so
    // labeling it as if it were would misrepresent what's actually being
    // counted.
    const nodeTagParts: string[] = [];
    if (protocolActive) nodeTagParts.push(PROTOCOL_LABEL[protocolFilter]);
    if (regionActive) nodeTagParts.push(selectedRegions.size === 1 ? [...selectedRegions][0] : `${selectedRegions.size} regions`);
    const nodeTag = nodeTagParts.join(" · ");
    nodesLabel = nodeTag ? `${nodeTag} nodes` : "Nodes";
    activeLabel = nodeTag ? `${nodeTag} active` : "Active";
  }

  // Packets: decoupled from the node `filtered` gate above, because a real
  // backend total is now available for EVERY protocol x provenance
  // combination — including the fully-"default" one (This radio only, all
  // protocols, no region), which `filtered` alone would still treat as
  // scoped (provenanceActive defaults true) but which the OLD code had no
  // server number for at all. Region is the only thing that still forces
  // the live-buffer fallback.
  // Deliberately excludes the radio-scope tag (PROVENANCE_SCOPE_LABEL) —
  // that scope is already shown by the "Filtered: This radio only" badge
  // below, so repeating it in the stat label itself was redundant and,
  // with the label's own all-caps CSS styling, unreadable ("THIS RADIO
  // ONLY PACKETS TOTAL").
  const packetTagParts: string[] = [];
  if (protocolActive) packetTagParts.push(PROTOCOL_LABEL[protocolFilter]);
  if (regionActive) packetTagParts.push(selectedRegions.size === 1 ? [...selectedRegions][0] : `${selectedRegions.size} regions`);
  const packetTag = packetTagParts.join(" · ");

  // Guards against showing a stale scope's numbers for one render right
  // after switching (protocol, provenance, or both), before the new poll
  // response for the NEW scope has landed.
  const backendScoped =
    !regionActive && scopedStats?.protocol === (protocolActive ? protocolFilter : null) && scopedStats?.provenance === provenanceScope
      ? scopedStats
      : null;

  if (backendScoped) {
    packetsTotal = backendScoped.packets_total;
    packetsRate = backendScoped.packets_per_min;
    packetsLabel = packetTag ? `${packetTag} packets` : "Packets";
    rateLabel = packetTag ? `${packetTag} packets/min` : "Packets/min";
  } else if (filtered) {
    // Reached either because region makes a real backend total
    // unavailable, or because the scoped fetch simply hasn't landed yet —
    // either way this is the live buffer, never a lifetime total, so it's
    // always labelled "(session)".
    const scoped = packets.filter((p) => {
      if (protocolActive && p.protocol !== protocolFilter) return false;
      if (!packetInProvenanceScope(p.trust_tier, provenanceScope)) return false;
      if (regionActive) {
        const fromNode = p.from_id != null ? nodes.get(String(p.from_id)) : undefined;
        if (!regionInScope(fromNode ?? null, { selectedCount: selectedRegions.size, regionsForNode })) return false;
      }
      return true;
    });
    packetsTotal = scoped.length;
    const now = Date.now();
    packetsRate = scoped.filter((p) => {
      const t = parseTimestamp(p.ts);
      return t !== null && now - t < 60000;
    }).length;
    packetsLabel = packetTag ? `${packetTag} packets (session)` : "Packets (session)";
    rateLabel = packetTag ? `${packetTag} packets/min (session)` : "Packets/min (session)";
  }

  return (
    <div className="status-strip" ref={rootRef}>
      {/* The four core counts get their own wrapping group before the
          controls below — at phone width (see .status-strip-stats in
          global.css) this wraps onto two rows of two, so every stat is
          fully readable. The controls, transport indicators, and filtered
          badge wrap into additional rows as needed. */}
      <div className="status-strip-stats">
        {/* 2026-09-30 data-layer fix: formatNumber/formatRate render "--"
            for null/undefined, which is exactly right once /api/stats has
            genuinely answered and a value is legitimately absent — but
            wrong on a truly first-ever visit (empty persisted cache,
            nothing fetched yet), where the owner's rule says a neutral
            skeleton, never "--"/zero. statsEverLoaded is false only in
            that first-ever-visit window; once /api/stats has resolved even
            once (including from the persisted cache, before this session's
            own network request lands), it's permanently true. */}
        <div className="status-item">
          <span className="value">{statsEverLoaded ? formatNumber(packetsTotal) : <span className="skeleton-value" aria-hidden="true" />}</span>
          <span className="label">{packetsLabel}</span>
        </div>
        <div className="status-item">
          <span className="value">{statsEverLoaded ? `${formatRate(packetsRate)}/min` : <span className="skeleton-value" aria-hidden="true" />}</span>
          <span className="label">{rateLabel}</span>
        </div>
        <div className="status-item">
          <span className="value">{statsEverLoaded ? formatNumber(nodesTotal) : <span className="skeleton-value" aria-hidden="true" />}</span>
          <span className="label">{nodesLabel}</span>
        </div>
        <div className="status-item">
          <span className="value">{statsEverLoaded ? formatNumber(nodesActive) : <span className="skeleton-value" aria-hidden="true" />}</span>
          <span className="label">{activeLabel}</span>
        </div>
      </div>
      {/* The Live Map's own per-view filter controls — moved here from
          the top bar's global chrome 2026-09-21 (protocol and region
          joined provenance for the same reason: route-gated to Live,
          per-view, and their effect already reports in the "Filtered:"
          badge below). Grouped together rather than split near the
          spacer/badge on the right, to keep them next to the counts they
          affect without depending on flex-spacer wrap behaviour. */}
      <RegionFilter />
      <ProtocolToggle />
      <TagFilter />
      <RegionReachControl />
      <ProvenanceToggle />
      {health?.transports?.map((t) => (
        <div className="status-item" key={t.id}>
          <span className="value" style={{ fontSize: 12 }}>
            {t.state}
          </span>
          <span className="label">
            {t.id} · {relativeTime(t.last_packet_at)}
          </span>
        </div>
      ))}
      {overflowCount > 0 && (
        <div className="status-item">
          <span className="value" style={{ color: "#c98500" }}>
            {overflowCount}
          </span>
          <span className="label">Anim. skipped</span>
        </div>
      )}
      {/* Stale-data note (data-layer slice 2, 2026-09-30) — null (nothing
          rendered) whenever the global data is younger than 2x the
          backend's audit.recompute_interval_minutes setting; see
          lib/definitions.ts's staleDataNote for the exact rule. Reuses
          BackboneTab's own .backbone-stale-warning style verbatim (plain
          small amber text, no box) rather than inventing a new "something's
          off" visual. */}
      {staleNote && <span className="backbone-stale-warning">{staleNote}</span>}
      <div className="spacer" />
      {filtered && (
        <div className="filter-badge">
          Filtered:{" "}
          {[
            protocolActive ? `${PROTOCOL_LABEL[protocolFilter]} only` : null,
            provenanceActive ? PROVENANCE_SCOPE_LABEL.connected_radio : null,
            regionActive ? `${[...selectedRegions].join(", ")}` : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </div>
      )}
      <div className="ws-indicator" data-state={wsState}>
        <span className="ws-dot" />
        {wsLabel}
      </div>
    </div>
  );
}
